import React from 'react';
import styled from 'styled-components';
import moment from 'moment';
import MediaQuery from 'react-responsive';
import { media } from 'helpers/MediaHelper';

import Select from 'components/ui/SelectField';
import DatePicker from 'components/ui/DatePicker';
import CtaText from 'components/ui/CtaCard';
import TextBox from 'components/ui/TextBox';
import Row from 'components/ui/Row';
import Col from 'components/ui/Col';
import { VerticalFormWrapper } from 'components/ui/VerticalFormWrapper';

import { ButtonPrimary } from '@yuhu-inc/yuhui';
import {
  PURCHASE_ORDER_CATALOG_ITEM_FORM,
  NEW_PURCHASE_ORDER_CATALOG_ITEM_FORM,
} from './AddPurchaseOrderDrawer';
import Form from 'components/ui/Form';
import { getTranslatedString, getCurrentLocale } from 'helpers/i18n';

const StyledError = styled.div`
  text-align: center;
  color: var(--yuhu-red);
  margin-bottom: -16px;
  margin-top: 24px;
`;

const AddPOButtonContainer = styled.div`
  margin-bottom: 24px;
`;

const StyledFormRow = styled.div`
  flex-direction: row;
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  ${media.mobile`
    flex-direction: column;
  `};
`;

const StyledFormRowWithColumns = styled(Row)``;

const FullWidthInput = styled(TextBox)`
  width: 100%;
`;

const FullWidthTextArea = styled(TextBox.TextArea)`
  width: 100%;
`;

const StyledFormItem = styled(Form.Item)`
  width: 100%;
  padding: 0px 8px;
  text-align: ${(props) => props.textAlign || 'left'};
  margin: ${(props) => props.margin || '0px'};
`;

const StyledFormItemLabel = styled.div`
  margin-bottom: 5px;
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
`;

const StyledMobileItemCtaSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  height: 60px;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.03);
  border-radius: 8px;
  justify-content: space-between;
  padding: 0px 15px;
`;

export const PurchaseOrderForm = ({
  values,
  setFieldValue,
  properties,
  vendors,
  setPurchaseOrderFormStep,
  handlePropertyChange,
  errors,
  touched,
  resetPurchaseOrderItemList,
  vendorHasCatalogItems,
  setVendorHasCatalogItems,
  disabledPropertySelect,
}) => {
  return (
    <VerticalFormWrapper style={{ width: '100%' }}>
      <StyledFormRow>
        <StyledFormItem>
          <Form.Item
            label={`${getTranslatedString('landlordPortal.purchaseOrderForm.title')}*`}
            validateStatus={touched.title && errors.title ? 'error' : null}
            help={touched.title && errors.title}
          >
            <FullWidthInput
              type="text"
              value={values.title}
              placeholder={getTranslatedString(
                'landlordPortal.purchaseOrderForm.title.placeholder'
              )}
              onChange={(e) => {
                setFieldValue('title', e.target.value);
              }}
              size="large"
            />
          </Form.Item>
        </StyledFormItem>
      </StyledFormRow>
      <StyledFormRow>
        <StyledFormItem>
          <Form.Item
            label={`${getTranslatedString('landlordPortal.purchaseOrderForm.description')}*`}
            validateStatus={touched.description && errors.description ? 'error' : null}
            help={touched.description && errors.description}
          >
            <FullWidthTextArea
              name="description"
              value={values.description}
              placeholder={getTranslatedString(
                'landlordPortal.purchaseOrderForm.additionalDetails'
              )}
              onChange={(event) => setFieldValue('description', event.currentTarget.value)}
              size="large"
            />
          </Form.Item>
        </StyledFormItem>
      </StyledFormRow>
      <StyledFormRow>
        <StyledFormItem>
          <Form.Item
            label={`${getTranslatedString('landlordPortal.purchaseOrderForm.vendor')}*`}
            validateStatus={touched.vendor_id && errors.vendor_id ? 'error' : null}
            help={touched.vendor_id && errors.vendor_id}
          >
            <Select
              name="vendor_id"
              style={{ borderColor: 'red' }}
              showSearch
              placeholder={getTranslatedString('landlordPortal.purchaseOrderForm.select')}
              size="large"
              value={values.vendor_id}
              onChange={(value) => {
                let vendor = vendors.find((vendor) => vendor.id === value);
                let uniqueCatalogCategories = vendor.catalog_items.reduce(
                  (acc, x) =>
                    acc.concat(
                      acc.find((y) => y.item_category_id === x.item_category_id) ? [] : [x]
                    ),
                  []
                );
                setVendorHasCatalogItems(uniqueCatalogCategories.length > 0);
                setFieldValue('vendor_name', vendor.name);
                setFieldValue('vendor_id', value);
                resetPurchaseOrderItemList();
              }}
              filterOption={(input, option) =>
                option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {vendors.map((vendor, i) => (
                <Select.Option value={vendor.id} key={`${vendor.id}_${i}`}>
                  {vendor.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </StyledFormItem>
      </StyledFormRow>
      <StyledFormRowWithColumns>
        <Col span={12}>
          <StyledFormItem>
            <Form.Item
              label={`${getTranslatedString('landlordPortal.purchaseOrderForm.requiredDate')}*`}
              validateStatus={touched.required_date && errors.required_date ? 'error' : null}
              help={touched.required_date && errors.required_date}
            >
              <StyledDatePicker
                error={touched.required_date && errors.required_date}
                name="required_date"
                size="large"
                value={values.required_date}
                defaultPickerValue={moment().locale(getCurrentLocale())}
                showToday={false}
                placeholder="eg. 2020/01/01"
                mobileMode="date"
                disabledDate={(current) => current < moment().startOf('day')}
                minDate={new Date()}
                onChange={(value) => setFieldValue('required_date', value)}
              />
            </Form.Item>
          </StyledFormItem>
        </Col>
        <Col span={12}>
          <StyledFormItem
            label={`${getTranslatedString('landlordPortal.purchaseOrderForm.property')}*`}
            validateStatus={touched.property_id && errors.property_id ? 'error' : null}
            help={touched.property_id && errors.property_id}
          >
            <Select
              name="property_id"
              showSearch
              placeholder={getTranslatedString('landlordPortal.purchaseOrderForm.select')}
              size="large"
              value={values.property_id}
              onChange={handlePropertyChange}
              disabled={disabledPropertySelect}
              filterOption={(input, option) =>
                option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {properties.map((property, i) => (
                <Select.Option value={property.value} key={`${property.value}_${i}`}>
                  {property.label}
                </Select.Option>
              ))}
            </Select>
          </StyledFormItem>
        </Col>
      </StyledFormRowWithColumns>
      {values.purchase_order_items.length === 0 && (
        <AddPOButtonContainer>
          <StyledError>
            {getTranslatedString('landlordPortal.addPurchaseOrderDrawer.addCatalogItemRequired')}
          </StyledError>
          <StyledFormRow>
            <MediaQuery query="(min-device-width: 750px)">
              <StyledFormItem textAlign="center">
                <ButtonPrimary
                  type="button"
                  style={{ padding: '1px 50px', marginTop: '25px' }}
                  onClick={() =>
                    vendorHasCatalogItems
                      ? setPurchaseOrderFormStep(PURCHASE_ORDER_CATALOG_ITEM_FORM)
                      : setPurchaseOrderFormStep(NEW_PURCHASE_ORDER_CATALOG_ITEM_FORM)
                  }
                  contentKey="landlordPortal.addPurchaseOrderDrawer.addCatalogItem"
                  disabled={!values.vendor_id}
                />
              </StyledFormItem>
            </MediaQuery>
            <MediaQuery query="(max-device-width: 750px)">
              <StyledFormItem>
                <StyledFormItemLabel>
                  {getTranslatedString('landlordPortal.newPurchaseOrderCatalogItem.items')}*
                </StyledFormItemLabel>
                <StyledMobileItemCtaSection>
                  <div>
                    {getTranslatedString('landlordPortal.newPurchaseOrderCatalogItem.noPartsAdded')}
                  </div>
                  <CtaText
                    type="button"
                    onClick={() =>
                      vendorHasCatalogItems
                        ? setPurchaseOrderFormStep(PURCHASE_ORDER_CATALOG_ITEM_FORM)
                        : setPurchaseOrderFormStep(NEW_PURCHASE_ORDER_CATALOG_ITEM_FORM)
                    }
                    style={{ padding: '0px' }}
                    disabled={!values.vendor_id}
                  >
                    {getTranslatedString('landlordPortal.newPurchaseOrderCatalogItem.addPart')}
                  </CtaText>
                </StyledMobileItemCtaSection>
              </StyledFormItem>
            </MediaQuery>
          </StyledFormRow>
        </AddPOButtonContainer>
      )}
    </VerticalFormWrapper>
  );
};
